import { Fragment, useMemo, useState, useSyncExternalStore, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Copy,
  Download,
  Info,
  Lightbulb,
  OctagonAlert,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/utils";
import { downloadPlatformSkill } from "./api";
import { DOCS_BASE, slugify } from "./paths";
import { highlight } from "./highlight";
import "./highlight.css";
import type { CalloutVariant, DocBlock, TimelineActor } from "./types";

/** The chosen platform tab (Node.js, iOS, ...) is shared by every code block, and remembered. */
const TAB_KEY = "dev-docs:platform-tab";
const tabListeners = new Set<() => void>();
const getPreferredTab = () => {
  try {
    return localStorage.getItem(TAB_KEY);
  } catch {
    return null;
  }
};
const setPreferredTab = (label: string) => {
  try {
    localStorage.setItem(TAB_KEY, label);
  } catch {
    /* storage unavailable */
  }
  tabListeners.forEach((fn) => fn());
};
const usePreferredTab = () =>
  useSyncExternalStore(
    (fn) => {
      tabListeners.add(fn);
      return () => tabListeners.delete(fn);
    },
    getPreferredTab
  );

/** Inline markup: `code`, **bold** and [label](#page-id | https://...) */
export const Inline = ({ text }: { text: string }) => {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("`") && part.endsWith("`") && part.length > 1) {
          return (
            <code
              key={i}
              className="px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800/80 text-[0.85em] font-mono text-slate-800 dark:text-slate-200 break-words"
            >
              {part.slice(1, -1)}
            </code>
          );
        }
        if (part.startsWith("**") && part.endsWith("**") && part.length > 3) {
          return (
            <strong key={i} className="font-semibold text-slate-900 dark:text-white">
              {part.slice(2, -2)}
            </strong>
          );
        }
        const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (link) {
          const [, label, href] = link;
          const cls = "font-medium text-orange-600 dark:text-orange-400 hover:underline";
          return href.startsWith("#") ? (
            <Link key={i} to={`${DOCS_BASE}/${href.slice(1)}`} className={cls}>
              {label}
            </Link>
          ) : (
            <a key={i} href={href} target="_blank" rel="noreferrer" className={cls}>
              {label}
            </a>
          );
        }
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
};

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 hover:text-slate-200 transition-colors"
      aria-label="Copy code"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

const CodeBlock = ({
  title,
  lang,
  code,
  tabs,
}: {
  title?: string;
  lang?: string;
  code?: string;
  tabs?: { label: string; lang: string; code: string }[];
}) => {
  const preferred = usePreferredTab();
  const active = Math.max(0, tabs?.findIndex((t) => t.label === preferred) ?? 0);
  const current = tabs ? tabs[active] : { label: lang ?? "", lang: lang ?? "", code: code ?? "" };
  const html = useMemo(() => highlight(current.code, current.lang), [current.code, current.lang]);

  return (
    <div className="my-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-950 overflow-hidden shadow-sm">
      <div className="flex items-center justify-between gap-4 px-4 py-2.5 border-b border-slate-800 bg-slate-900/70">
        <div className="flex items-center gap-3 min-w-0">
          {title && (
            <span className="text-xs font-semibold text-slate-200 truncate">{title}</span>
          )}
          {tabs && (
            <div className="flex flex-wrap items-center gap-1">
              {tabs.map((tab, i) => (
                <button
                  key={tab.label}
                  type="button"
                  onClick={() => setPreferredTab(tab.label)}
                  className={cn(
                    "px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors",
                    i === active
                      ? "bg-orange-500/15 text-orange-400"
                      : "text-slate-400 hover:text-slate-200"
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
          {!tabs && lang && (
            <span className="text-[10px] uppercase tracking-widest text-slate-500">{lang}</span>
          )}
        </div>
        <CopyButton text={current.code} />
      </div>
      <pre className="docs-code p-4 overflow-x-auto text-[12.5px] leading-relaxed font-mono text-slate-200">
        <code dangerouslySetInnerHTML={{ __html: html }} />
      </pre>
    </div>
  );
};

const calloutStyles: Record<CalloutVariant, { box: string; icon: string; Icon: typeof Info }> = {
  info: {
    box: "bg-sky-50 dark:bg-sky-950/20 border-sky-200 dark:border-sky-900/40",
    icon: "text-sky-500",
    Icon: Info,
  },
  tip: {
    box: "bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/30",
    icon: "text-orange-500",
    Icon: Lightbulb,
  },
  warning: {
    box: "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/30",
    icon: "text-amber-500",
    Icon: AlertTriangle,
  },
  danger: {
    box: "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900/30",
    icon: "text-red-500",
    Icon: OctagonAlert,
  },
};

const actorStyles: Record<TimelineActor, { label: string; dot: string; badge: string }> = {
  user: {
    label: "User",
    dot: "bg-sky-500",
    badge: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
  },
  app: {
    label: "Your application",
    dot: "bg-emerald-500",
    badge: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  },
  platform: {
    label: "Platform",
    dot: "bg-orange-500",
    badge: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
  },
  admin: {
    label: "Administrator",
    dot: "bg-violet-500",
    badge: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  },
};

const methodStyles: Record<string, string> = {
  GET: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  POST: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
  PUT: "bg-sky-500/10 text-sky-600 dark:text-sky-400",
  DELETE: "bg-red-500/10 text-red-600 dark:text-red-400",
};

export const Block = ({ block }: { block: DocBlock }): ReactNode => {
  switch (block.type) {
    case "p":
      return (
        <p className="my-4 text-[14.5px] leading-7 text-slate-600 dark:text-slate-400">
          <Inline text={block.text} />
        </p>
      );

    case "h":
      return (
        <h2 id={slugify(block.text)} className="mt-10 mb-3 scroll-mt-6 pb-2 text-lg font-bold tracking-tight text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-800">
          {block.text}
        </h2>
      );

    case "list":
      return (
        <ul className="my-4 space-y-2.5">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-[14.5px] leading-7 text-slate-600 dark:text-slate-400">
              <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
              <span><Inline text={item} /></span>
            </li>
          ))}
        </ul>
      );

    case "steps":
      return (
        <ol className="my-6 space-y-5">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-4">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-500/10 text-xs font-bold text-orange-600 dark:text-orange-400 border border-orange-500/20">
                {i + 1}
              </span>
              <div className="min-w-0 pt-0.5">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{item.title}</p>
                <p className="mt-1 text-[14px] leading-6 text-slate-600 dark:text-slate-400">
                  <Inline text={item.text} />
                </p>
              </div>
            </li>
          ))}
        </ol>
      );

    case "table":
      return (
        <div className="my-5 overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-left text-[13px]">
            <thead className="bg-slate-50 dark:bg-slate-900/60">
              <tr>
                {block.columns.map((c) => (
                  <th key={c} className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
              {block.rows.map((row, i) => (
                <tr key={i} className="align-top">
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className={cn(
                        "px-4 py-3 leading-6 text-slate-600 dark:text-slate-400",
                        j === 0 && "font-medium text-slate-800 dark:text-slate-200"
                      )}
                    >
                      <Inline text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case "callout": {
      const { box, icon, Icon } = calloutStyles[block.variant] ?? calloutStyles.info;
      return (
        <div className={cn("my-5 flex gap-3 rounded-2xl border p-4", box)}>
          <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", icon)} />
          <div className="min-w-0 text-[14px] leading-6 text-slate-700 dark:text-slate-300">
            {block.title && (
              <p className="mb-0.5 font-semibold text-slate-900 dark:text-white">{block.title}</p>
            )}
            <Inline text={block.text} />
          </div>
        </div>
      );
    }

    case "code":
      return <CodeBlock title={block.title} lang={block.lang} code={block.code} />;

    case "code_tabs":
      return <CodeBlock title={block.title} tabs={block.tabs} />;

    case "endpoint":
      return (
        <div className="my-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 px-4 py-3">
          <span
            className={cn(
              "rounded-md px-2 py-0.5 text-[11px] font-bold tracking-wider",
              methodStyles[block.method] ?? methodStyles.GET
            )}
          >
            {block.method}
          </span>
          <code className="font-mono text-[13px] font-semibold text-slate-900 dark:text-white break-all">
            {block.path}
          </code>
          <p className="basis-full text-[13px] leading-6 text-slate-500 dark:text-slate-400">
            <Inline text={block.summary} />
          </p>
        </div>
      );

    case "facts":
      return (
        <dl className="my-5 grid gap-3 sm:grid-cols-2">
          {block.items.map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900/50"
            >
              <dt className="text-[11px] font-bold uppercase tracking-wider text-orange-600 dark:text-orange-400">
                {item.label}
              </dt>
              <dd className="mt-1.5 text-[13.5px] leading-6 text-slate-600 dark:text-slate-400">
                <Inline text={item.value} />
              </dd>
            </div>
          ))}
        </dl>
      );

    case "timeline":
      return (
        <ol className="my-8">
          {block.items.map((item, i) => {
            const actor = actorStyles[item.actor] ?? actorStyles.platform;
            const last = i === block.items.length - 1;
            return (
              <li key={i} className="relative flex gap-4 pb-8 last:pb-0 sm:gap-6">
                <div className="w-16 shrink-0 pt-1 text-right text-[12px] font-semibold tabular-nums text-slate-500 dark:text-slate-400 sm:w-24">
                  {item.time}
                </div>
                <div className="relative flex flex-col items-center">
                  <span className={cn("z-10 mt-1.5 h-3 w-3 rounded-full ring-4 ring-slate-50 dark:ring-slate-950", actor.dot)} />
                  {!last && <span className="absolute top-5 bottom-[-2rem] w-px bg-slate-200 dark:bg-slate-800" />}
                </div>
                <div className="min-w-0 flex-1">
                  <span className={cn("inline-block rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider", actor.badge)}>
                    {actor.label}
                  </span>
                  <h3 className="mt-1.5 text-[15px] font-semibold text-slate-900 dark:text-white">{item.title}</h3>
                  <p className="mt-1 text-[14px] leading-6 text-slate-600 dark:text-slate-400">
                    <Inline text={item.text} />
                  </p>
                  {item.code && <CodeBlock lang="http" code={item.code} />}
                  {item.chips && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {item.chips.map((chip) => (
                        <span
                          key={chip.label}
                          className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11.5px] dark:border-slate-800 dark:bg-slate-900"
                        >
                          <span className="text-slate-400">{chip.label}</span>
                          <span className="font-semibold text-slate-700 dark:text-slate-200">{chip.value}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      );

    case "skill_download":
      return <SkillDownload label={block.label} />;

    case "flow":
      return (
        <div className="my-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 p-5">
          {block.title && (
            <p className="mb-4 text-sm font-semibold text-slate-900 dark:text-white">{block.title}</p>
          )}
          <div className="mb-4 flex flex-wrap gap-2">
            {block.actors.map((actor) => (
              <span
                key={actor}
                className="rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-3 py-1 text-[11px] font-semibold text-slate-600 dark:text-slate-300"
              >
                {actor}
              </span>
            ))}
          </div>
          <ol className="space-y-3">
            {block.steps.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-500 text-[11px] font-bold text-white">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {step.from}
                    <ArrowRight className="h-3 w-3 text-orange-500" />
                    {step.to}
                  </p>
                  <p className="mt-0.5 text-[13.5px] leading-6 text-slate-600 dark:text-slate-400">
                    <Inline text={step.text} />
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      );

    default:
      return null;
  }
};

const SkillDownload = ({ label }: { label: string }) => {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await downloadPlatformSkill();
        } catch {
          toast.error("Could not download the skill package.");
        } finally {
          setBusy(false);
        }
      }}
      className="my-5 inline-flex items-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 disabled:opacity-60"
    >
      <Download className="h-4 w-4" />
      {label}
    </button>
  );
};
